import { useMemo } from "react";
import {
  MINUTE_MS,
  buildCoachAthleteCard,
  computeWeeklyTargets,
  type AthleteTargets,
  type CoachAthleteCard,
  type Locale,
  type ProgramRow,
} from "@heraion/shared";

import { buildMuscleCoverage } from "../Progress/muscleCoverage";
import { WHOLE_CATALOG } from "../Progress/muscleCoverageProposal";
import { useExerciseNameBySlug } from "../Logger/exerciseName";
import { useSessionResultsFromDeps } from "../Progress/useSessionResults";
import type { ProgressDeps } from "../Progress/types";
import { useNowTick } from "../useNowTick";

// What <AthleteDetail> derives from the athlete's session-results snapshot:
// the live header card, the coverage census and the weekly targets score.
// Split out of AthleteDetail.tsx (#25) to make room for the sessions section.

export type AthleteDetailDerivedInput = {
  card: CoachAthleteCard;
  deps: ProgressDeps;
  programs: ReadonlyArray<ProgramRow>;
  currentProgramId?: string | undefined;
  weekStartDay?: number | undefined;
  athleteTargets: AthleteTargets | null;
  locale: Locale;
};

export function useAthleteDetailDerived({
  card,
  deps,
  programs,
  currentProgramId,
  weekStartDay,
  athleteTargets,
  locale,
}: AthleteDetailDerivedInput) {
  // ONE fetch pair for every panel below: coverage reads the snapshot here, and
  // every mounted panel reads the same single-flight cache instance.
  // `error` is read, not dropped: the coverage hero is the first thing on
  // this screen now, so a failed read has to say so instead of rendering as
  // an absence (PG-8).
  const { results, catalog, error } = useSessionResultsFromDeps(deps);
  // ONE clock for the three below, re-read each minute: the last 7 days slide.
  const nowMs = useNowTick(true, MINUTE_MS);

  const coverage = useMemo(
    () =>
      results && catalog
        ? buildMuscleCoverage(
            results,
            catalog,
            nowMs,
            weekStartDay,
            athleteTargets,
            // A place never reaches a coach: the proposal reads the catalog.
            WHOLE_CATALOG,
          )
        : null,
    [results, catalog, nowMs, weekStartDay, athleteTargets],
  );
  // CO-7 / ME-3: the full weekly-targets score (every axis, not the muscle-only
  // coverage census) for the read-only targets panel. Same inputs as coverage,
  // so it rides the SAME session-results snapshot, no extra fetch. The TARGET
  // source is the athlete's own AthleteTargets (host-fetched). #393: the
  // pinned program no longer feeds it.
  const weeklyTargets = useMemo(
    () =>
      results && catalog
        ? computeWeeklyTargets(
            results,
            catalog,
            nowMs,
            {},
            athleteTargets,
          )
        : null,
    [results, catalog, nowMs, athleteTargets],
  );
  // Header from the same live snapshot as the panels (one as-of); the tap-time
  // `card` only stands in while the read loads.
  const liveCard = useMemo(
    () =>
      results && catalog
        ? buildCoachAthleteCard(
            {
              sub: card.sub,
              name: card.name,
              currentProgramId,
              sessions: results,
              catalog,
              programs,
              athleteTargets,
            },
            nowMs,
            locale,
          )
        : card,
    [
      card,
      results,
      catalog,
      nowMs,
      currentProgramId,
      programs,
      athleteTargets,
      locale,
    ],
  );
  // Resolve exercise-axis row labels BY SLUG against the live catalog (never a
  // write-time snapshot): the shared resolver Progress already uses.
  const resolveExerciseName = useExerciseNameBySlug(catalog);

  return {
    results,
    catalog,
    error,
    coverage,
    weeklyTargets,
    liveCard,
    resolveExerciseName,
  };
}
